import {
  API_URL,
  put,
  request,
  takeEvery,
  debug,
  createDoRequestAction,
  assertTotalSchema,
  v,
  SagaIterator,
  call,
  CreateDoRequestActionOutput,
} from "../../lib";
import { clearDb } from "../db";
import { clearLogoutPending } from "../../lib/auth/logout_pending";
import { withLoginClaim } from "../../lib/auth/pending_logout_retry";
import { startDeadlineRequest, startSignedInSession } from "../../lib/auth/session_deadlines";
import { toProofSafeSchemaError } from "../../model/auth_proof";

import {
  doAUTH_SET,
  doPROFILE_SET,
  doSETTINGS_SET,
  doMFA_REQUIRED_SET,
  doREQUEST_ERROR,
  doREQUEST_COMPLETE,
  doPROJECT_SETTINGS_LIST_SET,
  doFAVORITE_LIST_SET,
  doIDENTITY_SET,
  doUNREAD_ANNOUNCEMENT_RESET,
} from "../state";
import { doREQUEST_IDENTITY_UNREAD_ANNOUNCEMENT_LIST_FETCH } from "./identity_unread_announcement_list_fetch";

export const REQUEST_IDENTITY_AUTHENTICATE = "REQUEST_IDENTITY_AUTHENTICATE";
export const doREQUEST_IDENTITY_AUTHENTICATE =
  createDoRequestAction<IdentityAuthenticateRequestPayload>(REQUEST_IDENTITY_AUTHENTICATE);

export const requestIdentityAuthenticateActionCreators = {
  doREQUEST_IDENTITY_AUTHENTICATE,
};

export type IdentityAuthenticateRequestPayload = {
  email: string;
  password: string;
};

export function* requestIdentityAuthenticate(
  arg: CreateDoRequestActionOutput<IdentityAuthenticateRequestPayload>,
): SagaIterator {
  debug("saga*requestIdentityAuthenticate");
  const { payload, setRequestResult } = arg;

  try {
    assertTotalSchema({
      payload,
      schema: v.object({
        requestId: v.string().uuid().exist(),
        email: v
          .string()
          .email({ tlds: { allow: false } })
          .exist(),
        password: v.string().exist(),
      }),
    });
  } catch (error) {
    // the error reduced to `isJoi` and a message: the ValidationError itself quotes the rejected
    // payload, password included (see `toProofSafeSchemaError`)
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_IDENTITY_AUTHENTICATE,
          requestId: payload?.requestId,
          url: "/v1/web/identity-authenticate",
          error: toProofSafeSchemaError(error),
        },
        setRequestResult,
      ),
    );
  }

  const { email, password } = payload;

  // holds the login claim and waits out a logout retry in flight, so neither a retry already out
  // nor one started while this request is on the wire can clear its cookie; see `withLoginClaim`
  return yield* withLoginClaim(function* () {
    const deadlineRequest = startDeadlineRequest();
    const result = yield request({
      method: "post",
      url: `${API_URL}/v1/web/identity-authenticate`,
      data: {
        email,
        password,
      },
    });

    if (!result.success || result.error) {
      // never the raw error, which carries the request body (password included) on `config.data`,
      // and no `fromPayload`; see "Credential-Carrying Sagas" in src/store/saga/AGENTS.md
      debug("saga*requestIdentityAuthenticate failed", {
        statusCode: result.statusCode,
        type: result.type,
      });
      return yield put(
        doREQUEST_ERROR(
          {
            fromType: REQUEST_IDENTITY_AUTHENTICATE,
            requestId: payload.requestId,
            url: "/v1/web/identity-authenticate",
            statusCode: result.statusCode,
            type: result.type,
            payload: result.payload,
            headers: result.headers,
          },
          setRequestResult,
        ),
      );
    }

    const {
      profile,
      settings,
      projectSettingsList,
      workspaceFavoriteList,
      accessToken,
      accessTokenExpiresInSeconds,
      refreshTokenExpiresInSeconds,
      identity,
    } = result.payload;
    const { type } = result;

    if (type == "MFA_REQUIRED") {
      yield put(
        doREQUEST_COMPLETE(
          {
            fromType: REQUEST_IDENTITY_AUTHENTICATE,
            requestId: payload.requestId,
            ...result,
          },
          setRequestResult,
        ),
      );
      return yield put(
        doMFA_REQUIRED_SET({
          requestId: payload.requestId,
          ...result?.payload,
        }),
      );
    }

    yield call(clearDb);

    yield put(
      doAUTH_SET({
        accessToken,
        accessTokenExpiresInSeconds,
        refreshTokenExpiresInSeconds,
        ...startSignedInSession(result.payload, deadlineRequest),
      }),
    );

    // a fresh session is the other way the marker retires -- whatever the old session's logout
    // never managed to say, this tab is signed in again and must not sign itself out on the next boot
    yield call(clearLogoutPending);

    yield put(doPROFILE_SET(profile || {}));
    yield put(doSETTINGS_SET(settings));
    yield put(doPROJECT_SETTINGS_LIST_SET(projectSettingsList));
    yield put(doFAVORITE_LIST_SET(workspaceFavoriteList));
    yield put(doIDENTITY_SET(identity));

    // A previous login in this tab may have closed the promotion. Start this login clean, then fetch.
    yield put(doUNREAD_ANNOUNCEMENT_RESET());

    // Fetch unread announcements once after successful login
    // The accessToken is already available from the login response, so we can dispatch immediately
    debug("identity_authenticate: dispatching REQUEST_IDENTITY_UNREAD_ANNOUNCEMENT_LIST_FETCH");
    yield put(doREQUEST_IDENTITY_UNREAD_ANNOUNCEMENT_LIST_FETCH({ payload: {} }));

    yield put(
      doREQUEST_COMPLETE(
        {
          fromType: REQUEST_IDENTITY_AUTHENTICATE,
          requestId: payload.requestId,
          ...result,
        },
        setRequestResult,
      ),
    );
  });
}

export function* identityAuthenticateSaga() {
  yield takeEvery(REQUEST_IDENTITY_AUTHENTICATE, requestIdentityAuthenticate);
}
